<template>
  <section class="logo-gallery">
    <div class="logo-gallery__image-container">
      <div class="logo-gallery__image-grid">
        <img
          v-for="image in images.data"
          :key="`gallery-image-${image.filename}`"
          :src="`/company-logo/thumbnail/${image.filename}`"
          class="logo-gallery__image"
          :class="
            selectedImage && selectedImage.filename === image.filename
              ? 'logo-gallery__image-active'
              : ''
          "
          @click="selectedImage = image"
        />
      </div>
      <div class="logo-navigation-container">
        <span @click="getPreviousPage">
          <IconChevronCircleLeft
            :color="getLeftChevronColor"
            height="32"
            width="32"
          />
        </span>
        <span @click="getNextPage">
          <IconChevronCircleRight
            :color="getRightChevronColor"
            height="32"
            width="32"
          />
        </span>
      </div>
    </div>
    <div class="logo-gallery__selected-image">
      <img
        v-if="selectedImage"
        :src="`/company-logo/preview/${selectedImage.filename}`"
      />
      <button
        type="button"
        class="logo-gallery__delete-button"
        @click="deleteImage"
      >
        <IconDelete color="blue" />
      </button>
    </div>
    <BaseDialog
      ref="confirmation-dialog"
      :padding="false"
      class="image-dialog"
    />
  </section>
</template>

<script>
  import BaseButton from '~/components/base/BaseButton.vue';
  import IconDelete from '~/components/icon/IconDelete.vue';
  import IconChevronLeft from '~/components/icon/IconChevronLeft.vue';
  import IconChevronRight from '~/components/icon/IconChevronRight.vue';
  import BaseDialog from '~/components/base/BaseDialog.vue';
  import IconChevronCircleLeft from '~/components/icon/IconChevronCircleLeft.vue';
  import IconChevronCircleRight from '~/components/icon/IconChevronCircleRight.vue';

  export default {
    name: 'LogoGallery',
    components: {
      BaseButton,
      IconDelete,
      IconChevronLeft,
      IconChevronRight,
      BaseDialog,
      IconChevronCircleLeft,
      IconChevronCircleRight,
      IconChevronCircleRight,
      IconChevronCircleLeft,
    },
    props: {
      reportTypeId: {
        type: [Number, String],
        required: true,
      },
      currentlySelectedImage: {
        type: Object,
        default: null,
      },
      currentReportImage: {
        type: Object,
        default: null,
      },
    },
    data: () => ({
      images: [],
      selectedImage: null,
    }),
    watch: {
      selectedImage(val) {
        this.$emit('selected-image', val);
      },
    },
    created() {
      this.selectedImage = this.currentReportImage
        ? this.currentReportImage
        : this.currentlySelectedImage;
      this.getLogos();
    },
    computed: {
      getLeftChevronColor() {
        if (this.images.current_page === 1) {
          return 'disabled';
        }
        return 'charcoal';
      },
      getRightChevronColor() {
        if (this.images.current_page === this.images.last_page) {
          return 'disabled';
        }
        return 'charcoal';
      },
    },
    methods: {
      // TODO: REPLACE THIS WITH PINIA
      // ...mapMutations('reportLogos', {
      //   vxmRemoveReportTypeLogo: 'removeLogo'
      // }),
      async deleteImage() {
        const response = await this.$refs['confirmation-dialog'].prompt({
          title:
            '<span style="font-size: 30px; font-weight: 700">Are you sure?</span>',
          message: `<div style="font-size: 20px; font-weight: 700; color: var(--itr-charcoal); text-align: center;">Please confirm you would like to remove the logo associated with your report.</div>`,
          confirmText: 'Remove Logo',
          cancelText: 'Cancel',
          level: 'success',
        });
        if (response) {
          try {
            await axios.delete(`/company-logo/${this.selectedImage.filename}`);
            // TODO: REPLACE THIS WITH PINIA
            // this.vxmRemoveReportTypeLogo(this.reportTypeId)
            if (this.images.data.length === 1 && this.images.current_page > 1) {
              await this.getLogos(true);
            } else {
              await this.getLogos();
            }
            if (this.images.data.length) {
              this.selectedImage = this.images.data[0];
            } else {
              this.selectedImage = null;
              this.$emit('empty-images');
            }
          } catch (err) {
            // TODO: HANDLE ERROR - This is just silencing the error
          }
        }
      },
      async getLogos(previous = false) {
        try {
          let currentPage = this.images.current_page || 1;
          if (previous) {
            currentPage--;
          }
          const { data } = await axios.get(
            `/company-logo/${this.reportTypeId}?page=${currentPage}`
          );
          this.images = data;
          if (
            !this.selectedImage &&
            this.images.data &&
            this.images.data.length
          ) {
            this.selectedImage = this.images.data[0];
          }
        } catch (err) {
          // TODO: HANDLE ERROR - This is just silencing the error
        }
      },
      async getNextPage() {
        if (this.images.current_page === this.images.last_page) return;
        try {
          const nextPageUrl = this.images.next_page_url.replace(
            'http://',
            'https://'
          );
          const { data } = await axios.get(nextPageUrl);
          this.images = data;
        } catch (err) {
          // TODO: HANDLE ERROR - This is just silencing the error
        }
      },
      async getPreviousPage() {
        if (this.images.current_page === 1) return;
        try {
          const previousPageUrl = this.images.prev_page_url.replace(
            'http://',
            'https://'
          );
          const { data } = await axios.get(previousPageUrl);
          this.images = data;
        } catch (err) {
          // TODO: HANDLE ERROR - This is just silencing the error
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  .logo-gallery {
    display: flex;
    gap: 1rem;
    width: 100%;

    &__title {
      font-size: 1.5rem;
      font-weight: 700;
      margin-bottom: 1rem;
    }
    &__image-container {
      flex: 1;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      align-items: center;
      margin-top: 1rem;

      width: 100%;
      min-height: 265px;
    }
    &__image-grid {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      grid-gap: 1rem;
      margin-bottom: 1rem;
      width: 90%;
      height: 90%;
    }
    &__selected-image {
      position: relative;
      display: flex;
      justify-content: center;
      align-items: center;
      border: 3px solid var(--itr-blue);
      width: 33%;
      border-radius: 8px;
    }
    &__image {
      height: 100px;
      width: 100px;
      cursor: pointer;

      object-position: 50% 50%;
      object-fit: cover;
      border-radius: 8px;
    }
    &__image-active {
      border: 3px solid var(--itr-blue);
    }
    &__navigation-button {
      margin-bottom: 2rem;
    }
    &__delete-button {
      cursor: pointer;
      position: absolute;
      bottom: 0;
      right: 0;
      background-color: transparent;
      border-radius: 50%;
      border: none;
      width: 2.5rem;
      height: 2.5rem;
      display: flex;
      justify-content: center;
      align-items: center;
    }
  }
</style>
